import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "../../services/api";

export default function BannerCarousel() {
  var bannersState = useState([]);
  var banners = bannersState[0];
  var setBanners = bannersState[1];
  var imagesState = useState({});
  var images = imagesState[0];
  var setImages = imagesState[1];
  var currentState = useState(0);
  var current = currentState[0];
  var setCurrent = currentState[1];
  var containerRef = useRef(null);
  var timerRef = useRef(null);

  useEffect(function() {
    api.get("/banners/public").then(function(res) {
      var list = res.data.banners || [];
      setBanners(list);
      list.forEach(function(b) {
        api.get("/banners/" + b.id + "/image", { responseType: "blob" }).then(function(imgRes) {
          var url = URL.createObjectURL(imgRes.data);
          setImages(function(prev) {
            var next = {};
            for (var k in prev) next[k] = prev[k];
            next[b.id] = url;
            return next;
          });
        }).catch(function() {});
      });
    }).catch(function() {});
  }, []);

  var startTimer = useCallback(function() {
    if (timerRef.current) clearInterval(timerRef.current);
    if (banners.length <= 1) return;
    timerRef.current = setInterval(function() {
      setCurrent(function(prev) { return (prev + 1) % banners.length; });
    }, 5000);
  }, [banners.length]);

  useEffect(function() {
    startTimer();
    return function() { if (timerRef.current) clearInterval(timerRef.current); };
  }, [startTimer]);

  useEffect(function() {
    if (containerRef.current && banners.length > 0) {
      var el = containerRef.current;
      var w = el.offsetWidth;
      el.scrollTo({ left: current * w, behavior: "smooth" });
    }
  }, [current, banners.length]);

  function goTo(idx) {
    setCurrent(idx);
    startTimer();
  }

  function goPrev() {
    var idx = current <= 0 ? banners.length - 1 : current - 1;
    goTo(idx);
  }

  function goNext() {
    var idx = (current + 1) % banners.length;
    goTo(idx);
  }

  if (banners.length === 0) return null;

  return (
    <div className="relative rounded-2xl overflow-hidden mb-6 group" data-testid="banner-carousel">
      <div ref={containerRef} className="flex overflow-x-hidden snap-x snap-mandatory scroll-smooth" style={{ scrollSnapType: "x mandatory" }}>
        {banners.map(function(b, idx) {
          var imgSrc = images[b.id];
          var isLink = b.link_url && b.link_url.length > 0;
          var Wrapper = isLink ? "a" : "div";
          var wrapperProps = isLink ? { href: b.link_url, target: "_blank", rel: "noopener noreferrer" } : {};
          return (
            <Wrapper key={b.id} {...wrapperProps} className="flex-shrink-0 w-full snap-center" style={{ minWidth: "100%" }} data-testid={"banner-slide-" + idx}>
              {imgSrc ? (
                <img src={imgSrc} alt={b.title} className="w-full h-36 sm:h-44 lg:h-48 object-cover rounded-2xl" />
              ) : (
                <div className="w-full h-36 sm:h-44 lg:h-48 bg-gradient-to-r from-[#3B82F6] to-[#0A2F3D] rounded-2xl flex items-center justify-center">
                  <p className="text-white text-lg font-bold">{b.title}</p>
                </div>
              )}
            </Wrapper>
          );
        })}
      </div>

      {banners.length > 1 && (
        <>
          <button onClick={goPrev} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="banner-prev">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={goNext} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid="banner-next">
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {banners.map(function(_, idx) {
              var active = idx === current;
              return (
                <button key={idx} onClick={function() { goTo(idx); }}
                  className={"w-2 h-2 rounded-full transition-all " + (active ? "bg-white w-5" : "bg-white/50")}
                  data-testid={"banner-dot-" + idx} />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
